import React from 'react';
import { Rocket, Play, Loader, Download, Code, CheckCircle, Workflow, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Zap, AlertCircle, Database, Clock, ArrowRight, Copy, Check, Sparkles } from 'lucide-react';
import FlowVisualization from './FlowVisualization';

const ExecuteView = ({
  isDarkMode, agentId, workflowResult, currentFlowIndex, agentDefinition,
  generatedData, editedSamples, isExecuting, executionStatus, executionNodes,
  currentExecutingNodeIndex, executionCarouselIndex, switchingToNode,
  totalExecutionNodes, selectedExecutionSample, executionDisplayMode,
  executionDetailsExpanded, copiedSection, executionSidebarOpen, viewingHistoryRun,
  isPromptFlowExpanded, isExportingCode, exportProgress,
  // Callbacks
  setSelectedExecutionSample, setExecutionCarouselIndex, setIsPromptFlowExpanded,
  setExecutionDisplayMode, setExecutionDetailsExpanded, setCopiedSection,
  setSwitchingToNode, setCurrentView, onRunExecution, toggleExecutionSidebar,
  handleDownloadExecutionResults, handleExportCode, switchTimerRef,
}) => {

  // Helper to render execution I/O as a clean table
  const renderExecutionTable = (data, colorScheme = 'default') => {
    if (!data || typeof data !== 'object') {
      return <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No data</span>;
    }

    const formatValue = (value) => {
      if (value === null || value === undefined) return '-';
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (typeof value === 'number') return value.toLocaleString();
      if (Array.isArray(value)) {
        if (value.length === 0) return '-';
        if (typeof value[0] === 'object') return `${value.length} items`;
        return value.join(', ');
      }
      if (typeof value === 'object') return JSON.stringify(value);
      // Format timestamps
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(value)) {
        try {
          return new Date(value).toLocaleString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: 'numeric', minute: '2-digit'
          });
        } catch { return value; }
      }
      return String(value);
    };

    const borderColor = colorScheme === 'input'
      ? (isDarkMode ? 'border-blue-700/40' : 'border-blue-200/80')
      : (isDarkMode ? 'border-cyan-700/40' : 'border-cyan-200/80');

    const fadeGradient = colorScheme === 'input'
      ? (isDarkMode ? 'linear-gradient(to bottom, transparent, rgba(15,20,35,1))' : 'linear-gradient(to bottom, transparent, rgba(239,246,255,1))')
      : (isDarkMode ? 'linear-gradient(to bottom, transparent, rgba(15,25,30,1))' : 'linear-gradient(to bottom, transparent, rgba(236,254,255,1))');

    const TruncatableCell = ({ text }) => {
      const [expanded, setExpanded] = React.useState(false);
      const [copied, setCopied] = React.useState(false);
      const handleCellCopy = (e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      };
      if (text.length <= 80) return <span>{text}</span>;
      return (
        <div
          className={`cell-truncatable${expanded ? ' cell-expanded' : ''} group/cell relative`}
          onMouseEnter={() => setExpanded(true)}
          onMouseLeave={() => { setExpanded(false); setCopied(false); }}
          onClick={() => setExpanded(prev => !prev)}
        >
          {text}
          <div className="cell-fade" style={{ background: fadeGradient }} />
          <button
            onClick={handleCellCopy}
            className={`absolute top-1 right-1 p-1 rounded opacity-0 group-hover/cell:opacity-100 transition-opacity ${
              copied
                ? (isDarkMode ? 'bg-green-600/30 text-green-400' : 'bg-green-100 text-green-600')
                : (isDarkMode ? 'bg-gray-700/80 text-gray-300 hover:text-white' : 'bg-white/90 text-gray-500 hover:text-gray-800')
            } shadow-sm`}
            title={copied ? 'Copied!' : 'Copy value'}
          >
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          </button>
        </div>
      );
    };

    return (
      <table className={`w-full text-sm border-collapse`}>
        <tbody>
          {Object.entries(data).map(([key, value], idx) => {
            const formatted = formatValue(value);
            return (
              <tr key={key} className={idx > 0 ? `border-t ${borderColor}` : ''}>
                <td className={`py-2 pr-4 font-medium align-top whitespace-nowrap ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} style={{ width: '40%' }}>
                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim()}
                </td>
                <td className={`py-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  {typeof formatted === 'string' ? <TruncatableCell text={formatted} /> : formatted}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header Section */}
      <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-gradient-to-r from-cyan-900/50 to-blue-900/50 border border-cyan-700/50' : 'bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className={`text-xl font-bold flex items-center ${isDarkMode ? 'text-cyan-300' : 'text-cyan-700'}`}>
              <Rocket className="w-5 h-5 mr-2" />
              Run Agent
            </h2>
            <p className={`text-sm mt-1 ${isDarkMode ? 'text-cyan-200/70' : 'text-cyan-600'}`}>
              Execute your AI agent with sample data and watch it process through each node
            </p>
          </div>
          {/* Sample Selector */}
          <div className="flex items-center space-x-3">
            <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Input Sample:
            </label>
            <select
              value={selectedExecutionSample}
              onChange={(e) => setSelectedExecutionSample(Number(e.target.value))}
              disabled={isExecuting}
              className={`px-3 py-2 rounded-lg text-sm ${
                isDarkMode
                  ? 'bg-gray-800 border border-gray-600 text-gray-200'
                  : 'bg-white border border-gray-300 text-gray-800'
              } ${isExecuting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {(generatedData?.rows || editedSamples || [{ sample: 'Sample 1' }, { sample: 'Sample 2' }, { sample: 'Sample 3' }]).slice(0, 3).map((_, idx) => (
                <option key={idx} value={idx}>Sample {idx + 1}</option>
              ))}
            </select>
            <button
              onClick={() => onRunExecution(selectedExecutionSample)}
              disabled={isExecuting}
              className={`px-5 py-2 rounded-lg font-medium flex items-center space-x-2 ${
                isExecuting
                  ? 'bg-gray-500 cursor-not-allowed text-gray-300'
                  : 'bg-[#0077A8] hover:bg-[#006090] text-white'
              }`}
            >
              {isExecuting ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Executing...</span>
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  <span>Execute</span>
                </>
              )}
            </button>
            <button
              onClick={handleExportCode}
              disabled={isExportingCode || !agentId}
              className={`px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors ${
                isExportingCode || !agentId
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
              } ${
                isDarkMode
                  ? 'bg-emerald-700 hover:bg-emerald-600 text-white'
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              }`}
            >
              {isExportingCode ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              <span>{isExportingCode ? 'Generating...' : 'Export Code'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Code Export Progress Skeleton */}
      {isExportingCode && exportProgress && (
        <div className={`rounded-lg p-5 border ${
          isDarkMode
            ? 'bg-gray-800/80 border-emerald-700/50'
            : 'bg-white border-emerald-200'
        }`}>
          <div className="flex items-center space-x-3 mb-4">
            <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-emerald-900/50' : 'bg-emerald-50'}`}>
              <Code className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
            </div>
            <div>
              <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
                Exporting Full-Stack Code
              </h3>
              <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {exportProgress.message}
              </p>
            </div>
          </div>
          {/* Progress steps */}
          <div className="space-y-3">
            {[
              { key: 'loading', label: 'Loading agent definition' },
              { key: 'generating', label: 'Generating backend & frontend code' },
              { key: 'parsing', label: 'Parsing generated files' },
              { key: 'packaging', label: 'Packaging zip archive' },
            ].map((s, i) => {
              const steps = ['loading', 'generating', 'parsing', 'packaging', 'downloading'];
              const currentIdx = steps.indexOf(exportProgress.step);
              const stepIdx = steps.indexOf(s.key);
              const isDone = stepIdx < currentIdx;
              const isActive = stepIdx === currentIdx;
              return (
                <div key={s.key} className="flex items-center space-x-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isDone
                      ? (isDarkMode ? 'bg-emerald-600' : 'bg-emerald-500')
                      : isActive
                        ? (isDarkMode ? 'bg-emerald-800 border-2 border-emerald-400' : 'bg-emerald-100 border-2 border-emerald-500')
                        : (isDarkMode ? 'bg-gray-700' : 'bg-gray-200')
                  }`}>
                    {isDone ? (
                      <CheckCircle className="w-3.5 h-3.5 text-white" />
                    ) : isActive ? (
                      <Loader className={`w-3.5 h-3.5 animate-spin ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                    ) : (
                      <span className={`w-2 h-2 rounded-full ${isDarkMode ? 'bg-gray-600' : 'bg-gray-400'}`} />
                    )}
                  </div>
                  <span className={`text-sm ${
                    isDone
                      ? (isDarkMode ? 'text-emerald-400' : 'text-emerald-600')
                      : isActive
                        ? (isDarkMode ? 'text-white font-medium' : 'text-gray-900 font-medium')
                        : (isDarkMode ? 'text-gray-500' : 'text-gray-400')
                  }`}>
                    {s.label}
                  </span>
                  {isActive && (
                    <div className="flex-1 h-1.5 rounded-full overflow-hidden ml-2" style={{ maxWidth: '120px' }}>
                      <div className={`h-full rounded-full animate-pulse ${isDarkMode ? 'bg-emerald-600' : 'bg-emerald-300'}`}
                        style={{ width: '60%', animation: 'pulse 1.5s ease-in-out infinite' }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Collapsible Flow Visualization - simple header */}
      <div
        className="group rounded-lg px-4 py-3 cursor-pointer transition-all duration-300 hover:brightness-110 hover:shadow-lg"
        style={{
          background: isDarkMode
            ? 'rgba(30, 41, 59, 0.9)'
            : 'linear-gradient(135deg, #0891b2 0%, #06b6d4 50%, #22d3ee 100%)'
        }}
        onClick={() => setIsPromptFlowExpanded(!isPromptFlowExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Workflow className={`w-4 h-4 ${isDarkMode ? 'text-gray-300' : 'text-white'}`} />
            <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-white'}`}>
              Flow Visualization
            </span>
            {executionStatus === 'running' && (
              <span className="flex items-center space-x-1 text-xs text-cyan-300">
                <Loader className="w-3 h-3 animate-spin" />
                <span>Running...</span>
              </span>
            )}
            {executionStatus === 'completed' && (
              <span className="flex items-center space-x-1 text-xs text-green-300">
                <CheckCircle className="w-3 h-3" />
                <span>Completed</span>
              </span>
            )}
          </div>
          {/* Expand/collapse hint */}
          <div className="flex items-center space-x-2 flex-shrink-0 transition-all duration-300">
            <span className={`text-xs font-medium opacity-0 group-hover:opacity-100 transition-all duration-300 ${
              isDarkMode ? 'text-white' : 'text-white'
            }`}>
              {isPromptFlowExpanded ? 'collapse' : 'expand'}
            </span>
            {isPromptFlowExpanded ? (
              <ChevronUp className={`w-4 h-4 transition-all duration-300 group-hover:-translate-y-0.5 ${
                isDarkMode ? 'text-gray-400 group-hover:text-white' : 'text-white/80 group-hover:text-white'
              }`} />
            ) : (
              <ChevronDown className={`w-4 h-4 transition-all duration-300 group-hover:translate-y-0.5 ${
                isDarkMode ? 'text-gray-400 group-hover:text-white' : 'text-white/80 group-hover:text-white'
              }`} />
            )}
          </div>
        </div>
      </div>

      {/* Expanded: Full FlowVisualization */}
      {isPromptFlowExpanded && (
        <div className={`rounded-lg transition-all duration-300 ${isDarkMode ? 'bg-gray-800/30 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
          <div className="px-4 py-4" style={{ overflow: 'visible', minHeight: '200px' }}>
            <FlowVisualization
              nodes={(workflowResult?.flows?.[currentFlowIndex]?.workflow_steps || workflowResult?.primary_workflow || []).map((step, idx) => ({
                nodeId: step.node?.node_id || `node-${idx}`,
                alias: step.node?.display_name || `Node ${idx + 1}`,
                description: step.node?.business_function || ''
              }))}
              workflowSteps={workflowResult?.flows?.[currentFlowIndex]?.workflow_steps || workflowResult?.primary_workflow || []}
              isDarkMode={isDarkMode}
              agentDefinition={agentDefinition}
              variableMappings={null}
              currentNodeIndex={executionCarouselIndex}
              onNodeClick={(idx) => setExecutionCarouselIndex(idx)}
              executionNodes={executionNodes}
            />
          </div>
        </div>
      )}

      {/* Execution Progress Bar - visible during and after execution */}
      {(isExecuting || executionNodes.length > 0) && (() => {
        const completedCount = executionNodes.filter(n => n && (n.status === 'completed' || n.status === 'failed')).length;
        const failedCount = executionNodes.filter(n => n && n.status === 'failed').length;
        const total = totalExecutionNodes || executionNodes.length || 1;
        const progressPct = (completedCount / total) * 100;
        const runningNodeName = isExecuting && currentExecutingNodeIndex >= 0
          ? executionNodes[currentExecutingNodeIndex]?.alias || `Node ${currentExecutingNodeIndex + 1}`
          : null;
        const isComplete = executionStatus === 'completed' || executionStatus === 'failed';
        const hasFailed = failedCount > 0;

        return (
          <div className={`rounded-lg sticky top-0 z-30 backdrop-blur-xl ${isExecuting ? 'sticky-glow' : ''} ${isDarkMode ? 'bg-gray-800/80 border border-gray-700/80' : 'bg-white/80 border border-gray-200/80'}`}>
            <div className="px-4 py-3">
              {/* Status text row */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  {isExecuting ? (
                    <>
                      <Loader className={`w-4 h-4 animate-spin ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-cyan-300' : 'text-cyan-700'}`}>
                        Processing {runningNodeName || '...'}
                      </span>
                    </>
                  ) : isComplete && !hasFailed ? (
                    <>
                      <CheckCircle className={`w-4 h-4 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                        Execution complete
                      </span>
                    </>
                  ) : hasFailed ? (
                    <>
                      <AlertCircle className={`w-4 h-4 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                        {failedCount} node{failedCount > 1 ? 's' : ''} failed
                      </span>
                    </>
                  ) : (
                    <>
                      <Zap className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Ready to execute
                      </span>
                    </>
                  )}
                </div>
                <span className={`text-xs font-mono ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {completedCount}/{total} nodes
                </span>
              </div>
              {/* Progress bar track */}
              <div className={`h-2.5 rounded-full relative overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                {/* Completed fill */}
                <div
                  className="h-full absolute top-0 left-0 rounded-full transition-all duration-700 ease-out"
                  style={{
                    width: `${progressPct}%`,
                    background: hasFailed
                      ? 'linear-gradient(90deg, #ef4444, #f87171)'
                      : isComplete
                      ? 'linear-gradient(90deg, #22c55e, #4ade80)'
                      : 'linear-gradient(90deg, #0ea5e9, #06b6d4, #22d3ee)',
                  }}
                />
                {/* Animated pulse for currently running segment */}
                {isExecuting && progressPct < 100 && (
                  <div
                    className="h-full absolute top-0 rounded-full"
                    style={{
                      left: `${progressPct}%`,
                      width: `${(1 / total) * 100}%`,
                      background: 'linear-gradient(90deg, rgba(14, 165, 233, 0.5), rgba(34, 211, 238, 0.3))',
                      animation: 'executionPulse 1.5s ease-in-out infinite',
                    }}
                  />
                )}
              </div>
              {/* Node step indicators */}
              <div className="flex justify-between mt-2">
                {(executionNodes.length > 0
                  ? executionNodes
                  : Array.from({ length: totalExecutionNodes || 0 }, (_, i) => ({ nodeId: `pending-${i}`, alias: null, status: 'pending' }))
                ).map((node, idx) => {
                  const isRunning = isExecuting && idx === currentExecutingNodeIndex;
                  return (
                    <button
                      key={node?.nodeId || idx}
                      onClick={() => executionNodes.length > 0 && setExecutionCarouselIndex(idx)}
                      className={`flex items-center space-x-1 text-xs px-1.5 py-0.5 rounded transition-colors ${
                        executionCarouselIndex === idx && executionNodes.length > 0
                          ? isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-900'
                          : isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        node?.status === 'completed' ? 'bg-green-400' :
                        node?.status === 'failed' ? 'bg-red-400' :
                        isRunning ? 'bg-cyan-400 animate-pulse' :
                        isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
                      }`} />
                      <span className="truncate max-w-[80px]">{node?.alias || `Node ${idx + 1}`}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Switching info bar */}
            {switchingToNode && (
              <div
                className={`flex items-center justify-between px-4 py-1.5 text-xs border-t ${
                  isDarkMode
                    ? 'bg-cyan-950/80 text-cyan-300 border-cyan-800/50'
                    : 'bg-cyan-50/90 text-cyan-700 border-cyan-200'
                }`}
              >
                <span className="font-medium">
                  Moving to {switchingToNode.alias}...
                </span>
                <button
                  onClick={() => {
                    if (switchTimerRef.current) {
                      clearTimeout(switchTimerRef.current);
                      switchTimerRef.current = null;
                    }
                    setSwitchingToNode(null);
                  }}
                  className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                    isDarkMode
                      ? 'bg-gray-700/80 hover:bg-gray-600 text-gray-300'
                      : 'bg-white hover:bg-gray-100 text-gray-600 shadow-sm'
                  }`}
                >
                  Stay here
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {/* Execution Results - Carousel View */}
      {executionNodes.length > 0 && (
        <div className={`rounded-lg ${isExecuting ? 'execution-glow' : ''} ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>

          {/* Header with node carousel navigation */}
          <div className={`px-4 py-3 border-b ${isDarkMode ? 'bg-gray-800/80 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className={`text-sm font-semibold flex items-center ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                <Database className="w-4 h-4 mr-2" />
                Execution Results
                {executionStatus === 'completed' && (
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-400 border border-green-500/30">
                    Completed
                  </span>
                )}
                {executionStatus === 'failed' && (
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-red-500/20 text-red-400 border border-red-500/30">
                    Failed
                  </span>
                )}
                {viewingHistoryRun && (
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30">
                    Past Run
                  </span>
                )}
              </h3>
              <button
                onClick={toggleExecutionSidebar}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium flex items-center space-x-1.5 transition-colors ${
                  executionSidebarOpen
                    ? isDarkMode
                      ? 'bg-cyan-900/50 text-cyan-400 border border-cyan-700'
                      : 'bg-cyan-50 text-cyan-600 border border-cyan-300'
                    : isDarkMode
                      ? 'bg-gray-700 hover:bg-cyan-900/40 text-gray-400 hover:text-cyan-400 border border-gray-600 hover:border-cyan-700'
                      : 'bg-gray-100 hover:bg-cyan-50 text-gray-500 hover:text-cyan-600 border border-gray-300 hover:border-cyan-300'
                }`}
                title="View past execution runs"
              >
                <Clock className="w-3.5 h-3.5" />
                <span>Past Runs</span>
              </button>
            </div>

            {/* Simple carousel navigation - prev/next only */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setExecutionCarouselIndex(Math.max(0, executionCarouselIndex - 1))}
                disabled={executionCarouselIndex === 0}
                className={`p-1.5 rounded-lg transition-colors ${executionCarouselIndex === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-700'}`}
              >
                <ChevronLeft className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>

              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {executionCarouselIndex + 1} / {executionNodes.length}
              </span>

              <button
                onClick={() => setExecutionCarouselIndex(Math.min(executionNodes.length - 1, executionCarouselIndex + 1))}
                disabled={executionCarouselIndex >= executionNodes.length - 1}
                className={`p-1.5 rounded-lg transition-colors ${executionCarouselIndex >= executionNodes.length - 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-700'}`}
              >
                <ChevronRight className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>
            </div>
          </div>

          {/* Current node content */}
          {executionNodes[executionCarouselIndex] && (
            <div className="p-5">
              {(() => {
                const nodeExec = executionNodes[executionCarouselIndex];
                return (
                  <>
                    {/* Node header with status */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          nodeExec.status === 'correcting' ? 'bg-amber-500/20' :
                          nodeExec.status === 'running' ? 'bg-cyan-500/20' :
                          nodeExec.status === 'completed' ? 'bg-green-500/20' :
                          nodeExec.status === 'failed' ? 'bg-red-500/20' : 'bg-gray-700'
                        }`}
                        style={nodeExec.status === 'correcting' ? {
                          animation: 'pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                          boxShadow: '0 0 12px rgba(245, 158, 11, 0.4)'
                        } : nodeExec.status === 'running' ? {
                          animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                        } : {}}>
                          {nodeExec.status === 'correcting' && <Zap className="w-5 h-5 animate-pulse text-amber-400" />}
                          {nodeExec.status === 'running' && <Loader className="w-5 h-5 animate-spin text-cyan-400" />}
                          {nodeExec.status === 'completed' && <CheckCircle className="w-5 h-5 text-green-400" />}
                          {nodeExec.status === 'failed' && <AlertCircle className="w-5 h-5 text-red-400" />}
                        </div>
                        <div>
                          <h4 className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                            Node {executionCarouselIndex + 1}: {nodeExec.alias}
                          </h4>
                          <div className="flex items-center space-x-2 mt-0.5">
                            <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'}`}>
                              {nodeExec.nodeType}
                            </span>
                            {nodeExec.durationMs && (
                              <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                {(nodeExec.durationMs / 1000).toFixed(2)}s
                              </span>
                            )}
                            {nodeExec.validation && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                nodeExec.validation.tier === 'green' ? 'bg-green-500/20 text-green-400' :
                                nodeExec.validation.tier === 'amber' ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-red-500/20 text-red-400'
                              }`}>
                                Quality: {nodeExec.validation.totalScore}/100
                              </span>
                            )}
                            {nodeExec.status === 'correcting' && nodeExec.brainwaveMessage && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 animate-pulse">
                                <Zap className="w-3 h-3 inline mr-1" />{nodeExec.brainwaveMessage}
                              </span>
                            )}
                            {nodeExec.brainwaveCorrected && nodeExec.status === 'completed' && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300">
                                <Zap className="w-3 h-3 inline mr-1" />Brainwave Corrected
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Brainwave Correction Banner */}
                    {nodeExec.brainwaveCorrected && nodeExec.correctionDetails && (
                      <div className={`rounded-lg p-4 mb-4 ${isDarkMode ? 'bg-gradient-to-r from-amber-900/30 to-orange-900/30 border border-amber-800/50' : 'bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200'}`}>
                        <div className="flex items-start space-x-3">
                          <div className={`p-2 rounded-lg flex-shrink-0 ${isDarkMode ? 'bg-amber-500/20' : 'bg-amber-100'}`}>
                            <Zap className={`w-4 h-4 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-2">
                              <h5 className={`text-sm font-semibold ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                                Brainwave Self-Correction
                              </h5>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                nodeExec.correctionDetails.tier === 'green' ? 'bg-green-500/20 text-green-400' :
                                nodeExec.correctionDetails.tier === 'amber' ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-red-500/20 text-red-400'
                              }`}>
                                Score: {nodeExec.correctionDetails.correctedScore}/100
                              </span>
                            </div>
                            <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {nodeExec.correctionDetails.changesMade}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* TLDR Section - LLM generated summary / skeleton placeholder */}
                    {(nodeExec.status === 'completed' || nodeExec.status === 'running' || nodeExec.status === 'correcting') && (
                      <div className={`rounded-lg p-4 mb-4 ${isDarkMode ? 'bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border border-cyan-800/50' : 'bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200'}`}>
                        <div className="flex items-start space-x-3">
                          <div className={`p-2 rounded-lg flex-shrink-0 ${isDarkMode ? 'bg-cyan-500/20' : 'bg-cyan-100'}`}>
                            <Sparkles className={`w-4 h-4 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h5 className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-cyan-300' : 'text-cyan-700'}`}>
                              Summary
                            </h5>
                            {(nodeExec.status === 'running' || nodeExec.status === 'correcting') ? (
                              <div className="space-y-2">
                                <div className={`h-3 rounded-full w-3/4 animate-pulse ${isDarkMode ? 'bg-cyan-800/40' : 'bg-cyan-200/60'}`} />
                                <div className={`h-3 rounded-full w-1/2 animate-pulse ${isDarkMode ? 'bg-cyan-800/30' : 'bg-cyan-200/40'}`} />
                                <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  Byte's output analysis will appear here once processing completes...
                                </p>
                              </div>
                            ) : nodeExec.tldrLoading ? (
                              <div className="space-y-2">
                                <div className="flex items-center space-x-2">
                                  <Loader className={`w-4 h-4 animate-spin ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
                                  <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                    Generating summary...
                                  </span>
                                </div>
                                <div className={`h-3 rounded-full w-3/4 animate-pulse ${isDarkMode ? 'bg-cyan-800/40' : 'bg-cyan-200/60'}`} />
                                <div className={`h-3 rounded-full w-5/6 animate-pulse ${isDarkMode ? 'bg-cyan-800/30' : 'bg-cyan-200/40'}`} />
                              </div>
                            ) : nodeExec.tldr ? (
                              <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                {nodeExec.tldr}
                              </p>
                            ) : (
                              <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                Summary not available
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Details Section - Expandable */}
                    <div className={`rounded-lg border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <button
                        onClick={() => setExecutionDetailsExpanded(!executionDetailsExpanded)}
                        className={`w-full px-4 py-3 flex items-center justify-between ${isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'} rounded-t-lg`}
                      >
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Details
                        </span>
                        <div className="flex items-center space-x-3">
                          {/* View Mode Toggle */}
                          <div className={`flex items-center rounded-md p-0.5 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} onClick={e => e.stopPropagation()}>
                            <button
                              onClick={() => setExecutionDisplayMode('table')}
                              className={`px-2 py-1 text-xs font-medium rounded transition-all ${
                                executionDisplayMode === 'table'
                                  ? (isDarkMode ? 'bg-gray-600 text-white' : 'bg-white text-gray-700 shadow-sm')
                                  : (isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700')
                              }`}
                            >
                              Table
                            </button>
                            <button
                              onClick={() => setExecutionDisplayMode('json')}
                              className={`px-2 py-1 text-xs font-medium rounded transition-all ${
                                executionDisplayMode === 'json'
                                  ? (isDarkMode ? 'bg-gray-600 text-white' : 'bg-white text-gray-700 shadow-sm')
                                  : (isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700')
                              }`}
                            >
                              JSON
                            </button>
                          </div>
                          {/* Download CSV */}
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDownloadExecutionResults(); }}
                            className={`p-1.5 rounded transition-colors ${isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'}`}
                            title="Download results as CSV"
                          >
                            <Download className="w-3.5 h-3.5" />
                          </button>
                          {executionDetailsExpanded ? (
                            <ChevronUp className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                          ) : (
                            <ChevronDown className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                          )}
                        </div>
                      </button>

                      {executionDetailsExpanded && (
                        <div className={`px-4 pb-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                          <div className="grid grid-cols-2 gap-4 pt-4">
                            {/* Input */}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <label className={`text-xs font-semibold flex items-center ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                  <ArrowRight className="w-3 h-3 mr-1" />
                                  INPUT
                                </label>
                                {nodeExec.input && (
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(JSON.stringify(nodeExec.input, null, 2));
                                      setCopiedSection('input-' + executionCarouselIndex);
                                      setTimeout(() => setCopiedSection(null), 1500);
                                    }}
                                    className={`p-1 rounded transition-colors ${
                                      copiedSection === 'input-' + executionCarouselIndex
                                        ? (isDarkMode ? 'text-green-400' : 'text-green-600')
                                        : (isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600')
                                    }`}
                                    title={copiedSection === 'input-' + executionCarouselIndex ? 'Copied!' : 'Copy all input'}
                                  >
                                    {copiedSection === 'input-' + executionCarouselIndex ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                  </button>
                                )}
                              </div>
                              <div className={`rounded-lg p-4 max-h-64 overflow-y-auto ${isDarkMode ? 'bg-blue-900/15 border border-blue-700/30' : 'bg-blue-50/80 border border-blue-200'}`}>
                                {executionDisplayMode === 'json' ? (
                                  <pre className={`text-xs font-mono whitespace-pre-wrap ${isDarkMode ? 'text-blue-300' : 'text-blue-900'}`}>
                                    {JSON.stringify(nodeExec.input, null, 2)}
                                  </pre>
                                ) : (
                                  renderExecutionTable(nodeExec.input, 'input')
                                )}
                              </div>
                            </div>

                            {/* Output */}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <label className={`text-xs font-semibold flex items-center ${
                                  nodeExec.status === 'failed'
                                    ? (isDarkMode ? 'text-red-400' : 'text-red-600')
                                    : (isDarkMode ? 'text-cyan-400' : 'text-cyan-700')
                                }`}>
                                  <ArrowRight className="w-3 h-3 mr-1" />
                                  OUTPUT
                                </label>
                                {nodeExec.output && nodeExec.status !== 'running' && nodeExec.status !== 'correcting' && (
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(JSON.stringify(nodeExec.output, null, 2));
                                      setCopiedSection('output-' + executionCarouselIndex);
                                      setTimeout(() => setCopiedSection(null), 1500);
                                    }}
                                    className={`p-1 rounded transition-colors ${
                                      copiedSection === 'output-' + executionCarouselIndex
                                        ? (isDarkMode ? 'text-green-400' : 'text-green-600')
                                        : (isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600')
                                    }`}
                                    title={copiedSection === 'output-' + executionCarouselIndex ? 'Copied!' : 'Copy all output'}
                                  >
                                    {copiedSection === 'output-' + executionCarouselIndex ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                  </button>
                                )}
                              </div>
                              <div className={`rounded-lg p-4 max-h-64 overflow-y-auto ${
                                nodeExec.status === 'failed'
                                  ? (isDarkMode ? 'bg-red-900/10 border border-red-900/30' : 'bg-red-50 border border-red-200')
                                  : nodeExec.status === 'correcting'
                                    ? (isDarkMode ? 'bg-amber-900/10 border border-amber-900/30' : 'bg-amber-50 border border-amber-200')
                                    : nodeExec.status === 'running'
                                      ? (isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gray-100 border border-gray-200')
                                      : (isDarkMode ? 'bg-cyan-900/15 border border-cyan-700/30' : 'bg-cyan-50/80 border border-cyan-200')
                              }`}>
                                {nodeExec.status === 'correcting' ? (
                                  <div className="flex items-center space-x-2 py-4 justify-center">
                                    <Zap className="w-4 h-4 animate-pulse text-amber-400" />
                                    <span className={`text-sm ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>Brainwave correcting...</span>
                                  </div>
                                ) : nodeExec.status === 'running' ? (
                                  <div className="flex items-center space-x-2 py-4 justify-center">
                                    <Loader className="w-4 h-4 animate-spin text-cyan-400" />
                                    <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Processing...</span>
                                  </div>
                                ) : nodeExec.status === 'failed' ? (
                                  <pre className={`text-xs font-mono whitespace-pre-wrap ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                                    {nodeExec.error}
                                  </pre>
                                ) : executionDisplayMode === 'json' ? (
                                  <pre className={`text-xs font-mono whitespace-pre-wrap ${isDarkMode ? 'text-cyan-300' : 'text-cyan-900'}`}>
                                    {JSON.stringify(nodeExec.output, null, 2)}
                                  </pre>
                                ) : (
                                  renderExecutionTable(nodeExec.output, 'output')
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {executionNodes.length === 0 && !isExecuting && (
        <div className={`rounded-lg p-12 text-center ${isDarkMode ? 'bg-gray-800/30 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
          <Rocket className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
          <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Ready to Execute
          </h3>
          <p className={`text-sm max-w-md mx-auto ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            Select a sample input and click "Execute" to run your AI agent. You'll see each node process in real-time with input/output data.
          </p>
        </div>
      )}

      {/* Bottom Navigation */}
      <div className="flex items-center justify-between pt-4">
        <button
          onClick={() => setCurrentView('prompts')}
          className={`px-4 py-2 rounded-lg flex items-center space-x-2 ${
            isDarkMode
              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
              : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
          }`}
        >
          <ChevronLeft className="w-4 h-4" />
          <span>Back to Prompts</span>
        </button>
        <button
          onClick={() => setCurrentView('prototype')}
          className="px-5 py-2 rounded-lg font-medium bg-[#0077A8] hover:bg-[#006090] text-white flex items-center space-x-2 transition-colors"
        >
          <Sparkles className="w-4 h-4" />
          <span>Generate Prototype</span>
        </button>
      </div>
    </div>
  );
};

export default ExecuteView;
